import { useState, useMemo } from 'react'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/shared/components/ui/table'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/shared/components/ui/button'
import { Input } from '@/shared/components/ui/input'
import { Badge } from '@/shared/components/ui/badge'
import { Skeleton } from '@/shared/components/ui/skeleton'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ui/select'
import {
  Search,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  ArrowUp,
  ArrowDown,
  ArrowUpDown,
  Eye,
  Calendar,
  User,
  Mail,
  MapPin,
  Store,
  Building2,
  UserCheck,
  Trophy,
  Pill,
  Hash,
  Star,
  Loader2,
  FileText,
  ShieldCheck,
  Tag,
  DollarSign,
  Gift,
  type LucideIcon,
} from 'lucide-react'
import { useDashboardTabla } from '../hooks/useDashboard'
import type { DashboardTablaDatos } from '../types'

const iconMap: Record<string, LucideIcon> = {
  fecha: Calendar,
  'nombre completo': User,
  correo: Mail,
  provincia: MapPin,
  farmacia: Store,
  codigo_farmacia: Hash,
  'codigo farmacia': Hash,
  nombre_farmacia: Building2,
  direccion: MapPin,
  promotor: UserCheck,
  total: Trophy,
  puntos: Star,
  'puntos canjeados': Gift,
  'total usd': DollarSign,
  medicamento: Pill,
  cantidad: Hash,
  'estado': ShieldCheck,
  'tipo': Tag,
}

/** Columnas que cuando isCurrency se formatean como moneda (con $) */
const CURRENCY_COLUMNS = new Set(['total', 'cantidad'])

/** Columnas numéricas que se formatean con max 2 decimales pero sin $ */
const NUMERIC_COLUMNS = new Set(['puntos', 'puntos_canjeados', 'total'])

/** Columna virtual calculada: puntos_canjeados * 0.25 */
const VIRTUAL_COLUMN = 'total_usd'

/** Labels personalizados para columnas (solo con isCurrency) */
const LABEL_MAP: Record<string, string> = {
  cantidad: 'total',
}

/** Labels que siempre se aplican */
const LABEL_MAP_ALWAYS: Record<string, string> = {
  puntos_canjeados: 'puntos canjeados',
  total_usd: 'total usd',
  nombre_farmacia: 'nombre farmacia',
  codigo_farmacia: 'codigo farmacia',
}

function formatCellValue(col: string, value: unknown, isCurrency: boolean): string {
  if (value == null) return '-'
  const colLower = col.toLowerCase()
  if (isCurrency && CURRENCY_COLUMNS.has(colLower)) {
    const num = Number(value)
    if (!isNaN(num)) {
      const formatted = num.toLocaleString('es-EC', { minimumFractionDigits: 0, maximumFractionDigits: 2 })
      return `$ ${formatted}`
    }
  }
  if (NUMERIC_COLUMNS.has(colLower)) {
    const num = Number(value)
    if (!isNaN(num)) {
      return num.toLocaleString('es-EC', { minimumFractionDigits: 0, maximumFractionDigits: 2 })
    }
  }
  return String(value)
}

interface TablaFuenteProps {
  grupoId: string
  configId: number
  titulo: string
  camposMostrar: string[]
  onUsuarioClick?: (userId: number) => void
  onComprobanteClick?: (comprobanteId: number, medicamento?: string) => void
  isCurrency?: boolean
  filtros?: {
    fecha_desde?: string
    fecha_hasta?: string
    farmacia?: string
    provincia?: string
    promotor?: string
    nombre_farmacia?: string
    direccion?: string
  }
  allData?: DashboardTablaDatos
  allDataLoading?: boolean
  valorPuntoCanje?: number
}

function matchesBusqueda(row: Record<string, unknown>, term: string): boolean {
  const lower = term.toLowerCase()
  return Object.values(row).some(
    (val) => val != null && String(val).toLowerCase().includes(lower)
  )
}

function matchesFiltros(
  row: Record<string, unknown>,
  filtros: TablaFuenteProps['filtros']
): boolean {
  if (!filtros) return true

  if (filtros.farmacia) {
    const farmacias = filtros.farmacia.split(',').map((f) => f.trim())
    const val = String(row['farmacia'] || '').trim()
    if (!farmacias.includes(val)) return false
  }
  if (filtros.provincia) {
    const provincias = filtros.provincia.split(',').map((p) => p.trim())
    const val = String(row['provincia'] || '').trim()
    if (!provincias.includes(val)) return false
  }
  if (filtros.promotor) {
    const promotores = filtros.promotor.split(',').map((p) => p.trim())
    const val = String(row['promotor'] || '').trim()
    if (!promotores.includes(val)) return false
  }
  if (filtros.nombre_farmacia) {
    const vals = filtros.nombre_farmacia.split(',').map((f) => f.trim())
    const val = String(row['nombre_farmacia'] || '').trim()
    if (!vals.includes(val)) return false
  }
  if (filtros.direccion) {
    const vals = filtros.direccion.split(',').map((d) => d.trim())
    const val = String(row['direccion'] || '').trim()
    if (!vals.includes(val)) return false
  }
  if (filtros.fecha_desde || filtros.fecha_hasta) {
    // Buscar campo fecha en formato dd/mm/yyyy o yyyy-mm-dd
    const fechaRaw = String(row['fecha'] || '')
    let fechaStr = ''
    // Intentar formato dd/mm/yyyy → yyyy-mm-dd
    const ddmmyyyy = fechaRaw.match(/^(\d{2})\/(\d{2})\/(\d{4})$/)
    if (ddmmyyyy) {
      fechaStr = `${ddmmyyyy[3]}-${ddmmyyyy[2]}-${ddmmyyyy[1]}`
    } else {
      fechaStr = fechaRaw.slice(0, 10)
    }
    if (filtros.fecha_desde && fechaStr < filtros.fecha_desde) return false
    if (filtros.fecha_hasta && fechaStr > filtros.fecha_hasta) return false
  }
  return true
}

export function TablaFuente({
  grupoId,
  configId,
  titulo,
  camposMostrar,
  onUsuarioClick,
  onComprobanteClick,
  isCurrency = false,
  filtros,
  allData,
  allDataLoading,
  valorPuntoCanje = 0.25,
}: TablaFuenteProps) {
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [busqueda, setBusqueda] = useState('')
  const [sortKey, setSortKey] = useState<string | null>(null)
  const [sortAsc, setSortAsc] = useState(true)

  const isClientSide = !!allData

  // Fase 1: query paginada del servidor (se deshabilita cuando allData llega)
  const { data: serverData, isLoading } = useDashboardTabla(
    grupoId,
    isClientSide ? 0 : configId,
    {
      page,
      page_size: pageSize,
      busqueda: busqueda || undefined,
      ...filtros,
    }
  )

  // Fase 2: filtrado y ordenamiento client-side cuando allData está disponible
  const clientFiltered = useMemo(() => {
    if (!allData?.results) return null
    let rows = allData.results as Record<string, unknown>[]
    if (busqueda) {
      rows = rows.filter((r) => matchesBusqueda(r, busqueda))
    }
    rows = rows.filter((r) => matchesFiltros(r, filtros))
    if (sortKey) {
      const realKey = sortKey === VIRTUAL_COLUMN ? 'puntos_canjeados' : sortKey
      rows = [...rows].sort((a, b) => {
        const aVal = a[realKey]
        const bVal = b[realKey]
        const aNum = Number(aVal)
        const bNum = Number(bVal)
        if (!isNaN(aNum) && !isNaN(bNum)) {
          return sortAsc ? aNum - bNum : bNum - aNum
        }
        const aStr = String(aVal ?? '').toLowerCase()
        const bStr = String(bVal ?? '').toLowerCase()
        return sortAsc ? aStr.localeCompare(bStr) : bStr.localeCompare(aStr)
      })
    }
    return rows
  }, [allData, busqueda, filtros, sortKey, sortAsc])

  const clientPaginated = useMemo(() => {
    if (!clientFiltered) return null
    const start = (page - 1) * pageSize
    return clientFiltered.slice(start, start + pageSize)
  }, [clientFiltered, page, pageSize])

  // Datos a mostrar según fase
  const displayResults = isClientSide ? clientPaginated : serverData?.results
  const displayCount = isClientSide ? (clientFiltered?.length ?? 0) : (serverData?.count ?? 0)
  const displayTotalPages = isClientSide
    ? Math.max(1, Math.ceil((clientFiltered?.length ?? 0) / pageSize))
    : (serverData?.total_pages ?? 1)
  const displayPage = page

  // Use camposMostrar as base, then add any extra fields from data
  const hiddenColumns = new Set(['idcomprobante', 'fecha_creacion', 'idusuario', 'puntos'])
  const sampleRow = displayResults?.[0]
  const baseColumns = camposMostrar.length > 0 ? camposMostrar : (sampleRow ? Object.keys(sampleRow) : [])
  const extraColumns = sampleRow
    ? Object.keys(sampleRow).filter((k) => !baseColumns.includes(k))
    : []
  const rawColumns = [...baseColumns, ...extraColumns].filter((c) => !hiddenColumns.has(c))
  // Inyectar columna virtual "$ total" después de puntos_canjeados
  const hasPuntosCanjeados = rawColumns.includes('puntos_canjeados')
  const columns = hasPuntosCanjeados
    ? rawColumns.flatMap((c) => c === 'puntos_canjeados' ? [c, VIRTUAL_COLUMN] : [c])
    : rawColumns
  const hasComprobante = sampleRow != null && 'idcomprobante' in sampleRow

  const handleSearch = (value: string) => {
    setBusqueda(value)
    setPage(1)
  }

  const handleSort = (col: string) => {
    if (!isClientSide) return
    if (sortKey === col) {
      if (!sortAsc) {
        setSortKey(null)
        setSortAsc(true)
      } else {
        setSortAsc(false)
      }
    } else {
      setSortKey(col)
      setSortAsc(true)
    }
    setPage(1)
  }

  if (isLoading && !serverData && !allData) {
    return (
      <Card className="border border-gray-300 dark:border-gray-700">
        <CardHeader>
          <Skeleton className="h-6 w-48" />
        </CardHeader>
        <CardContent className="space-y-2">
          {[1, 2, 3, 4, 5].map((i) => (
            <Skeleton key={i} className="h-10 w-full" />
          ))}
        </CardContent>
      </Card>
    )
  }

  return (
    <Card className="border border-gray-300 dark:border-gray-700 w-full">
      <CardHeader className="pb-3">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-2">
            <CardTitle className="text-lg">{titulo}</CardTitle>
            <Badge variant="outline" className="text-xs font-semibold tabular-nums border-[0.5px] border-teal-500 rounded-full">
              {displayCount.toLocaleString()}
            </Badge>
            {allDataLoading && (
              <span className="flex items-center gap-1 text-xs text-muted-foreground">
                <Loader2 className="h-3 w-3 animate-spin" />
                Cargando completo...
              </span>
            )}
          </div>
          <div className="relative w-full sm:w-64">
            <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Buscar..."
              value={busqueda}
              onChange={(e) => handleSearch(e.target.value)}
              className="pl-8 h-8 text-sm"
            />
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="rounded-md border border-gray-300 dark:border-gray-700 overflow-x-auto">
          <Table>
            <TableHeader className="bg-gray-100 dark:bg-[hsl(220,15%,16%)]">
              <TableRow>
                {columns.map((col) => {
                  const colLower = col.toLowerCase()
                  const displayLabel = LABEL_MAP_ALWAYS[colLower] || (isCurrency && LABEL_MAP[colLower]) || colLower
                  const Icon = iconMap[displayLabel] || iconMap[colLower]
                  const isSorted = sortKey === col
                  const SortIcon = isSorted ? (sortAsc ? ArrowUp : ArrowDown) : ArrowUpDown
                  return (
                    <TableHead
                      key={col}
                      className={`select-none whitespace-nowrap ${isClientSide ? 'cursor-pointer hover:text-foreground' : ''}`}
                      onClick={() => handleSort(col)}
                    >
                      <div className="flex items-center gap-1.5 capitalize">
                        {Icon && <Icon className="h-3.5 w-3.5 text-muted-foreground" />}
                        {displayLabel}
                        {isClientSide && <SortIcon className={`h-3 w-3 ${isSorted ? 'text-teal-500' : 'text-muted-foreground/50'}`} />}
                      </div>
                    </TableHead>
                  )
                })}
                {onComprobanteClick && hasComprobante && <TableHead className="w-10" />}
                {onUsuarioClick && <TableHead className="w-10" />}
              </TableRow>
            </TableHeader>
            <TableBody>
              {displayResults?.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={columns.length + (onUsuarioClick ? 1 : 0) + (onComprobanteClick && hasComprobante ? 1 : 0)} className="text-center py-8 text-muted-foreground">
                    No se encontraron registros
                  </TableCell>
                </TableRow>
              ) : (
                displayResults?.map((row, idx) => (
                  <TableRow key={idx}>
                    {columns.map((col) => (
                      <TableCell key={col} className="whitespace-nowrap max-w-[300px] truncate">
                        {col === VIRTUAL_COLUMN ? (
                          <span className="font-semibold text-emerald-600 dark:text-emerald-400">
                            {((Number(row['puntos_canjeados']) || 0) * valorPuntoCanje).toLocaleString('en-US', { style: 'currency', currency: 'USD' })}
                          </span>
                        ) : col === 'estado' ? (
                          <Badge
                            variant="outline"
                            className={
                              String(row[col]).toLowerCase() === 'validado'
                                ? 'border-emerald-500 text-emerald-600 dark:text-emerald-400 text-xs'
                                : 'border-red-400 text-red-500 dark:text-red-400 text-xs'
                            }
                          >
                            {row[col] != null ? String(row[col]) : '-'}
                          </Badge>
                        ) : (
                          formatCellValue(col, row[col], isCurrency)
                        )}
                      </TableCell>
                    ))}
                    {onComprobanteClick && hasComprobante && (
                      <TableCell>
                        {row['idcomprobante'] != null && Number(row['idcomprobante']) > 0 && (
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7"
                            title="Ver factura"
                            onClick={() => onComprobanteClick(Number(row['idcomprobante']), row['medicamento'] ? String(row['medicamento']) : undefined)}
                          >
                            <FileText className="h-4 w-4" />
                          </Button>
                        )}
                      </TableCell>
                    )}
                    {onUsuarioClick && (
                      <TableCell>
                        {row['idusuario'] != null && (
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7"
                            onClick={() => onUsuarioClick(Number(row['idusuario']))}
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                        )}
                      </TableCell>
                    )}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>

        {/* Paginación */}
        <div className="flex items-center justify-between mt-4">
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground">Mostrar</span>
            <Select
              value={String(pageSize)}
              onValueChange={(v) => {
                setPageSize(Number(v))
                setPage(1)
              }}
            >
              <SelectTrigger className="w-[80px] h-8">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="10">10</SelectItem>
                <SelectItem value="25">25</SelectItem>
                <SelectItem value="50">50</SelectItem>
                <SelectItem value="100">100</SelectItem>
              </SelectContent>
            </Select>
            <span className="text-sm text-muted-foreground">por página</span>
          </div>
          <div className="flex items-center gap-1">
            <Button
              variant="outline"
              size="icon"
              className="h-8 w-8"
              onClick={() => setPage(1)}
              disabled={displayPage <= 1}
            >
              <ChevronsLeft className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              size="icon"
              className="h-8 w-8"
              onClick={() => setPage((p) => p - 1)}
              disabled={displayPage <= 1}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <span className="text-sm text-muted-foreground min-w-[100px] text-center">
              {displayPage} de {displayTotalPages}
            </span>
            <Button
              variant="outline"
              size="icon"
              className="h-8 w-8"
              onClick={() => setPage((p) => p + 1)}
              disabled={displayPage >= displayTotalPages}
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              size="icon"
              className="h-8 w-8"
              onClick={() => setPage(displayTotalPages)}
              disabled={displayPage >= displayTotalPages}
            >
              <ChevronsRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  )
}
